import React, { useCallback, memo, useState, useRef } from 'react';
import {
  Bold,
  Italic,
  Strikethrough,
  Eraser,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  List,
  ListOrdered,
  FileCode,
  Quote,
  Minus,
  VideoIcon,
  ImageIcon,
  Undo,
  Redo,
  Check,
  Link as LinkIcon,
  Palette,
  Mic,
  Loader2
} from 'lucide-react';
import AiMenu from './AiMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@core/components/ui/dropdown-menu';
import { Input } from '../../components/inputs/forms/Input';
import TemplateMenu from './TemplateMenu';
import { useAi } from '../../components/providers/AiProvider';

const ToolbarButton = memo(({ active, onClick, title, children }) => (
  <button
    onMouseDown={e => {
      e.preventDefault();
      onClick();
    }}
    className={`p-2 rounded hover:bg-base-200 ${active ? 'bg-base-200' : ''}`}
    title={title}
  >
    {children}
  </button>
));

const DropdownItemWithActiveCheck = memo(
  ({ onClick, title, active, children }) => (
    <DropdownMenuItem
      onSelect={e => {
        e.preventDefault();
        onClick();
      }}
      className='flex items-center justify-between gap-2'
      title={title}
    >
      {children}
      {active && <Check size={16} className='ml-auto' />}
    </DropdownMenuItem>
  )
);

const Toolbar = memo(
  ({
    editor,
    setShowVideoModal,
    setShowImageModal,
    setShowLinkModal,
    templates,
    templateCategories,
    defaultSelectedCategory,
    onTemplateSelect,
    onCreateTemplate,
    onDeleteTemplate,
    onUpdateTemplate
  }) => {
    if (!editor) {
      return null;
    }

    const { handleVoiceRequest } = useAi();

    const [recentColors, setRecentColors] = useState([]);
    const [hexColor, setHexColor] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    //const [audioURL, setAudioURL] = useState(''); // No longer needed
    const mediaRecorder = useRef(null);

    const setTextColor = useCallback(
      color => {
        if (color === null) {
          editor.chain().focus().unsetColor().run();
        } else {
          editor.chain().focus().setColor(color).run();
          setRecentColors(prevColors => {
            const newColors = [color, ...prevColors.filter(c => c !== color)];
            return newColors.slice(0, 5); // Keep only the last 5 colors
          });
        }
      },
      [editor]
    );

    const handleHexInputChange = e => {
      const value = e.target.value;
      setHexColor(value);
      if (/^#[0-9A-F]{6}$/i.test(value)) {
        setTextColor(value);
      }
    };

    const toggleBold = useCallback(() => {
      editor.chain().focus().toggleBold().run();
    }, [editor]);

    const toggleItalic = useCallback(() => {
      editor.chain().focus().toggleItalic().run();
    }, [editor]);

    const toggleStrike = useCallback(() => {
      editor.chain().focus().toggleStrike().run();
    }, [editor]);

    const clearFormatting = useCallback(() => {
      editor.chain().focus().unsetAllMarks().run();
    }, [editor]);

    const setParagraph = useCallback(() => {
      editor.chain().focus().setParagraph().run();
    }, [editor]);

    const setAlignLeft = useCallback(() => {
      editor.chain().focus().setTextAlign('left').run();
    }, [editor]);

    const setAlignCenter = useCallback(() => {
      editor.chain().focus().setTextAlign('center').run();
    }, [editor]);

    const setAlignRight = useCallback(() => {
      editor.chain().focus().setTextAlign('right').run();
    }, [editor]);

    const toggleHeading1 = useCallback(() => {
      editor.chain().focus().toggleHeading({ level: 1 }).run();
    }, [editor]);

    const toggleHeading2 = useCallback(() => {
      editor.chain().focus().toggleHeading({ level: 2 }).run();
    }, [editor]);

    const toggleHeading3 = useCallback(() => {
      editor.chain().focus().toggleHeading({ level: 3 }).run();
    }, [editor]);

    const toggleHeading4 = useCallback(() => {
      editor.chain().focus().toggleHeading({ level: 4 }).run();
    }, [editor]);

    const toggleHeading5 = useCallback(() => {
      editor.chain().focus().toggleHeading({ level: 5 }).run();
    }, [editor]);

    const toggleHeading6 = useCallback(() => {
      editor.chain().focus().toggleHeading({ level: 6 }).run();
    }, [editor]);

    const toggleBulletList = useCallback(() => {
      editor.chain().focus().toggleBulletList().run();
    }, [editor]);

    const toggleOrderedList = useCallback(() => {
      editor.chain().focus().toggleOrderedList().run();
    }, [editor]);

    const toggleCodeBlock = useCallback(() => {
      editor.chain().focus().toggleCodeBlock().run();
    }, [editor]);

    const toggleBlockquote = useCallback(() => {
      editor.chain().focus().toggleBlockquote().run();
    }, [editor]);

    const setHorizontalRule = useCallback(() => {
      editor.chain().focus().setHorizontalRule().run();
    }, [editor]);

    const handleVideoModal = useCallback(() => {
      setShowVideoModal(true);
    }, [setShowVideoModal]);

    const handleImageModal = useCallback(() => {
      setShowImageModal(true);
    }, [setShowImageModal]);

    const handleUndo = useCallback(() => {
      editor.chain().focus().undo().run();
    }, [editor]);

    const handleRedo = useCallback(() => {
      editor.chain().focus().redo().run();
    }, [editor]);

    const handleLinkModal = useCallback(() => {
      setShowLinkModal(true);
    }, [setShowLinkModal]);

    const getCurrentAlignmentIcon = () => {
      if (editor.isActive({ textAlign: 'center' })) {
        return <AlignCenter size={18} />;
      } else if (editor.isActive({ textAlign: 'right' })) {
        return <AlignRight size={18} />;
      } else {
        return <AlignLeft size={18} />;
      }
    };

    const handleVoice = useCallback(async () => {
      if (isProcessing) return;

      if (!isRecording) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true
          });
          mediaRecorder.current = new MediaRecorder(stream);
          const audioChunks = [];

          mediaRecorder.current.ondataavailable = event => {
            audioChunks.push(event.data);
          };

          mediaRecorder.current.onstop = async () => {
            const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });

            // Reset state
            setIsRecording(false);

            // Stop all tracks on the media stream
            stream.getTracks().forEach(track => track.stop());

            // Call handleVoiceRequest and insert the text into the editor
            try {
              setIsProcessing(true);
              const text = await handleVoiceRequest(audioBlob);
              editor.chain().focus().insertContent(text).run();
            } catch (error) {
              console.error('Error processing voice request:', error);
            } finally {
              setIsProcessing(false);
            }
          };

          mediaRecorder.current.start();
          setIsRecording(true);
        } catch (error) {
          console.error('Error accessing microphone:', error);
          setIsRecording(false);
          setIsProcessing(false);
        }
      } else {
        if (
          mediaRecorder.current &&
          mediaRecorder.current.state === 'recording'
        ) {
          mediaRecorder.current.stop();
        }
      }
    }, [isRecording, editor, isProcessing, handleVoiceRequest]);

    return (
      <div className='flex flex-col shadow'>
        <div className='flex flex-wrap gap-1 p-1 '>
          <ToolbarButton
            onClick={toggleBold}
            active={editor.isActive('bold')}
            title='Bold'
          >
            <Bold size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleItalic}
            active={editor.isActive('italic')}
            title='Italic'
          >
            <Italic size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleStrike}
            active={editor.isActive('strike')}
            title='Strikethrough'
          >
            <Strikethrough size={18} />
          </ToolbarButton>
          {/* Color dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`p-2 rounded hover:bg-base-200`}
                title='Text Color'
              >
                <Palette size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='max-w-40'>
              <div>
                <div className='grid grid-cols-5 gap-1 mb-2 justify-center items-center'>
                  {/* Predefined colors */}
                  <button
                    className='w-6 h-6 rounded-full bg-black hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#000000')}
                    aria-label='Set text color to Black'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-gray-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#6B7280')}
                    aria-label='Set text color to Gray'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-red-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#EF4444')}
                    aria-label='Set text color to Red'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-yellow-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#F59E0B')}
                    aria-label='Set text color to Yellow'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-green-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#10B981')}
                    aria-label='Set text color to Green'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-blue-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#3B82F6')}
                    aria-label='Set text color to Blue'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-indigo-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#6366F1')}
                    aria-label='Set text color to Indigo'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-purple-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#8B5CF6')}
                    aria-label='Set text color to Purple'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-pink-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#EC4899')}
                    aria-label='Set text color to Pink'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-teal-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#14B8A6')}
                    aria-label='Set text color to Teal'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-orange-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#F97316')}
                    aria-label='Set text color to Orange'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-lime-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#84CC16')}
                    aria-label='Set text color to Lime'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-emerald-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#10B981')}
                    aria-label='Set text color to Emerald'
                  />
                  <button
                    className='w-6 h-6 rounded-full bg-cyan-500 hover:ring-2 hover:ring-base-300'
                    onClick={() => setTextColor('#06B6D4')}
                    aria-label='Set text color to Cyan'
                  />
                </div>
                <div className=''>
                  <Input
                    type='text'
                    size='sm'
                    placeholder='#FFFFFF'
                    value={hexColor}
                    onChange={handleHexInputChange}
                    title='Enter a hex color value'
                  />

                  <div className='text-sm mb-1'>Recent Colors:</div>
                  <div className='flex gap-1'>
                    {recentColors.map((color, index) => (
                      <button
                        key={index}
                        className='w-6 h-6 rounded-full'
                        style={{ backgroundColor: color }}
                        onClick={() => setTextColor(color)}
                        aria-label={`Set text color to ${color}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <ToolbarButton
            onClick={clearFormatting}
            active={false}
            title='Clear Formatting'
          >
            <Eraser size={18} />
          </ToolbarButton>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`p-2 rounded hover:bg-base-200`}
                title='Text Styles'
              >
                <Heading size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownItemWithActiveCheck
                onClick={setParagraph}
                active={editor.isActive('paragraph')}
                title='Paragraph'
              >
                <Type size={18} className='mr-2' /> Paragraph
              </DropdownItemWithActiveCheck>
              <DropdownMenuSeparator />
              <DropdownItemWithActiveCheck
                onClick={toggleHeading1}
                active={editor.isActive('heading', { level: 1 })}
                title='Heading 1'
              >
                <Heading1 size={18} className='mr-2' /> Heading 1
              </DropdownItemWithActiveCheck>
              <DropdownItemWithActiveCheck
                onClick={toggleHeading2}
                active={editor.isActive('heading', { level: 2 })}
                title='Heading 2'
              >
                <Heading2 size={18} className='mr-2' /> Heading 2
              </DropdownItemWithActiveCheck>
              <DropdownItemWithActiveCheck
                onClick={toggleHeading3}
                active={editor.isActive('heading', { level: 3 })}
                title='Heading 3'
              >
                <Heading3 size={18} className='mr-2' /> Heading 3
              </DropdownItemWithActiveCheck>
              <DropdownItemWithActiveCheck
                onClick={toggleHeading4}
                active={editor.isActive('heading', { level: 4 })}
                title='Heading 4'
              >
                <Heading4 size={18} className='mr-2' /> Heading 4
              </DropdownItemWithActiveCheck>
              <DropdownItemWithActiveCheck
                onClick={toggleHeading5}
                active={editor.isActive('heading', { level: 5 })}
                title='Heading 5'
              >
                <Heading5 size={18} className='mr-2' /> Heading 5
              </DropdownItemWithActiveCheck>
              <DropdownItemWithActiveCheck
                onClick={toggleHeading6}
                active={editor.isActive('heading', { level: 6 })}
                title='Heading 6'
              >
                <Heading6 size={18} className='mr-2' /> Heading 6
              </DropdownItemWithActiveCheck>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Text Alignment Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`p-2 rounded hover:bg-base-200`}
                title='Text Alignment'
              >
                {getCurrentAlignmentIcon()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownItemWithActiveCheck
                onClick={setAlignLeft}
                active={editor.isActive({ textAlign: 'left' })}
                title='Align Left'
              >
                <AlignLeft size={18} className='mr-2' /> Align Left
              </DropdownItemWithActiveCheck>
              <DropdownItemWithActiveCheck
                onClick={setAlignCenter}
                active={editor.isActive({ textAlign: 'center' })}
                title='Align Center'
              >
                <AlignCenter size={18} className='mr-2' /> Align Center
              </DropdownItemWithActiveCheck>
              <DropdownItemWithActiveCheck
                onClick={setAlignRight}
                active={editor.isActive({ textAlign: 'right' })}
                title='Align Right'
              >
                <AlignRight size={18} className='mr-2' /> Align Right
              </DropdownItemWithActiveCheck>
            </DropdownMenuContent>
          </DropdownMenu>

          <ToolbarButton
            onClick={toggleBulletList}
            active={editor.isActive('bulletList')}
            title='Bullet List'
          >
            <List size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleOrderedList}
            active={editor.isActive('orderedList')}
            title='Numbered List'
          >
            <ListOrdered size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleCodeBlock}
            active={editor.isActive('codeBlock')}
            title='Code Block'
          >
            <FileCode size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleBlockquote}
            active={editor.isActive('blockquote')}
            title='Block Quote'
          >
            <Quote size={18} />
          </ToolbarButton>
          {/* Link handling */}
          <ToolbarButton
            onClick={handleLinkModal}
            active={false}
            title='Insert Link'
          >
            <LinkIcon size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={setHorizontalRule}
            active={false}
            title='Horizontal Rule'
          >
            <Minus size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleVideoModal}
            active={false}
            title='Add Video'
          >
            <VideoIcon size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleImageModal}
            active={false}
            title='Add Image'
          >
            <ImageIcon size={18} />
          </ToolbarButton>
          <ToolbarButton onClick={handleUndo} active={false} title='Undo'>
            <Undo size={18} />
          </ToolbarButton>
          <ToolbarButton onClick={handleRedo} active={false} title='Redo'>
            <Redo size={18} />
          </ToolbarButton>

          <AiMenu editor={editor} />

          <ToolbarButton
            onClick={handleVoice}
            active={isRecording}
            title='Voice'
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Loader2 size={18} className='animate-spin' />
            ) : isRecording ? (
              <div className='w-[15px] h-[15px] rounded-full bg-red-500 animate-pulse'></div>
            ) : (
              <Mic size={18} />
            )}
          </ToolbarButton>
          {templates && templateCategories && (
            <TemplateMenu
              editor={editor}
              templates={templates}
              templateCategories={templateCategories}
              onTemplateSelect={onTemplateSelect}
              onCreateTemplate={onCreateTemplate}
              onDeleteTemplate={onDeleteTemplate}
              onUpdateTemplate={onUpdateTemplate}
              defaultSelectedCategory={defaultSelectedCategory}
            />
          )}
        </div>
        {/*audioURL && <audio controls src={audioURL} />*/}
      </div>
    );
  }
);

export default Toolbar;
